/**
 * Unified Notification Platform façade (Phase 4.1).
 * Durable Firestore inbox + local mirror for snappy UI / offline MVP tests.
 * Domains call notify* helpers — they never write inbox collections directly.
 */

import { auth } from "../firebase/config";
import { fetchUserProfile, patchUserProfile } from "../repositories/userRepository";
import * as userNotificationRepository from "../repositories/userNotificationRepository";
import {
  NOTIFICATION_KINDS,
  NOTIFICATION_STATUSES,
  NOTIFICATION_GROUP_WINDOW_MS,
  MAX_NOTIFICATION_FANOUT,
  DEFAULT_NOTIFICATION_PREFERENCES,
  createNotificationShell,
  isCategoryEnabled,
  isNotificationUnread,
  isNotificationVisible,
  mergeNotificationPreferences,
  buildDedupeKey,
  resolveNotificationHref,
  groupNotificationsByDate,
  formatUnreadBadgeCount,
  shouldHideNotificationBell,
  getNotificationRegistryEntry,
} from "../domain/notifications/notificationTypes";

const STORAGE_KEY = "tra_notifications_v1";
const MAX_STORED = 40;

/** @type {((n: object) => void)[]} */
const listeners = [];

/** @type {object[] | null} */
let inboxCache = null;

function currentUid() {
  return auth.currentUser?.uid || null;
}

function readStore() {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function writeStore(list) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_STORED)));
  } catch {
    /* ignore quota */
  }
}

function emitLocal(payload) {
  listeners.forEach((fn) => {
    try {
      fn(payload);
    } catch {
      /* ignore */
    }
  });
}

function mirrorLocal(notification) {
  if (!notification?.notificationId) return;
  const next = [
    notification,
    ...readStore().filter((n) => n.notificationId !== notification.notificationId),
  ].slice(0, MAX_STORED);
  writeStore(next);
  inboxCache = normalizeList(next);
  emitLocal(notification);
}

function normalizeList(rows) {
  return (rows || [])
    .filter(isNotificationVisible)
    .sort((a, b) => (b.createdAtMs || 0) - (a.createdAtMs || 0));
}

/**
 * @param {(n: object) => void} listener
 * @returns {() => void}
 */
export function subscribeNotifications(listener) {
  listeners.push(listener);
  return () => {
    const i = listeners.indexOf(listener);
    if (i >= 0) listeners.splice(i, 1);
  };
}

/**
 * Subscribe durable inbox for signed-in user (preferred UI path).
 * @param {string} userId
 * @param {(rows: object[]) => void} onChange
 * @returns {() => void}
 */
export function subscribeInbox(userId, onChange) {
  if (!userId) {
    inboxCache = normalizeList(readStore());
    onChange(inboxCache);
    return subscribeNotifications(() => {
      inboxCache = normalizeList(readStore());
      onChange(inboxCache);
    });
  }
  return userNotificationRepository.subscribeUserNotifications(userId, (rows) => {
    inboxCache = normalizeList(rows);
    writeStore(inboxCache.slice(0, MAX_STORED));
    onChange(inboxCache);
    emitLocal({ type: "inbox_sync" });
  });
}

export function listNotifications() {
  if (inboxCache != null) return inboxCache;
  return normalizeList(readStore());
}

export function listUnreadCount() {
  return listNotifications().filter(isNotificationUnread).length;
}

/**
 * @param {string} [userId]
 */
export async function listForUser(userId) {
  const uid = userId || currentUid();
  if (!uid) return normalizeList(readStore());
  try {
    const rows = await userNotificationRepository.listUserNotifications(uid);
    inboxCache = normalizeList(rows);
    writeStore(inboxCache.slice(0, MAX_STORED));
    return inboxCache;
  } catch {
    return normalizeList(readStore());
  }
}

/**
 * @param {string} [userId]
 */
export async function getNotificationPreferences(userId) {
  const uid = userId || currentUid();
  if (!uid) return mergeNotificationPreferences(null);
  try {
    const profile = await fetchUserProfile(uid);
    return mergeNotificationPreferences(profile?.notificationPreferences);
  } catch {
    return mergeNotificationPreferences(null);
  }
}

/**
 * @param {object} prefs
 * @param {string} [userId]
 */
export async function saveNotificationPreferences(prefs, userId) {
  const uid = userId || currentUid();
  if (!uid) throw new Error("Sign in required.");
  const merged = mergeNotificationPreferences(prefs);
  await patchUserProfile(uid, { notificationPreferences: merged });
  return merged;
}

/**
 * Central emit — targeted fan-out, prefs, dedupe, optional grouping.
 * @param {object} input
 * @returns {Promise<object[]>}
 */
export async function notify(input = {}) {
  const kind = input.kind || NOTIFICATION_KINDS.SYSTEM;
  const recipientIds = [
    ...(Array.isArray(input.userIds) ? input.userIds : []),
    ...(input.userId ? [input.userId] : []),
  ]
    .map((id) => String(id || "").trim())
    .filter(Boolean);

  const uniqueRecipients = [...new Set(recipientIds)].slice(0, MAX_NOTIFICATION_FANOUT);

  if (!uniqueRecipients.length) {
    const shell = createNotificationShell({
      ...input,
      kind,
      notificationId: input.notificationId,
      userId: null,
    });
    if (!input.notificationId && !input.userId) {
      shell.notificationId = String(shell.notificationId).replace(/^unt_/, "ntf_");
    }
    mirrorLocal(shell);
    return [shell];
  }

  const created = [];
  for (const recipientId of uniqueRecipients) {
    try {
      const prefs = await getNotificationPreferences(recipientId);
      if (!isCategoryEnabled(prefs, kind)) continue;

      const dedupeKey =
        input.dedupeKey ||
        (input.entityId ? buildDedupeKey(kind, String(input.entityId)) : null);

      if (dedupeKey) {
        let existing = null;
        try {
          existing = await userNotificationRepository.findByDedupeKey(recipientId, dedupeKey);
        } catch {
          // Emitters may create another player's inbox row, but cannot list it.
          existing = null;
        }
        if (existing && isNotificationVisible(existing)) {
          created.push(existing);
          if (recipientId === currentUid()) mirrorLocal(existing);
          continue;
        }
      }

      if (input.groupKey) {
        const since = Date.now() - NOTIFICATION_GROUP_WINDOW_MS;
        let groupRow = null;
        try {
          groupRow = await userNotificationRepository.findActiveGroupNotification(
            recipientId,
            input.groupKey,
            since
          );
        } catch {
          groupRow = null;
        }
        if (groupRow) {
          const nextCount = (groupRow.groupCount || 1) + 1;
          const body =
            typeof input.groupBody === "function"
              ? input.groupBody(nextCount, groupRow)
              : input.body || groupRow.body;
          const title =
            typeof input.groupTitle === "function"
              ? input.groupTitle(nextCount, groupRow)
              : input.title || groupRow.title;
          await userNotificationRepository.updateUserNotification(groupRow.notificationId, {
            groupCount: nextCount,
            body,
            title,
            status: NOTIFICATION_STATUSES.UNREAD,
            read: false,
            createdAtMs: Date.now(),
          });
          const updated = {
            ...groupRow,
            groupCount: nextCount,
            body,
            title,
            status: NOTIFICATION_STATUSES.UNREAD,
            read: false,
            createdAtMs: Date.now(),
          };
          created.push(updated);
          if (recipientId === currentUid()) mirrorLocal(updated);
          continue;
        }
      }

      const shell = createNotificationShell({
        ...input,
        kind,
        userId: recipientId,
        dedupeKey,
        href: input.href || resolveNotificationHref({ ...input, kind }),
      });
      await userNotificationRepository.createUserNotification(shell);
      created.push(shell);
      if (recipientId === currentUid()) mirrorLocal(shell);
    } catch (err) {
      console.warn("[notifications] emit failed", recipientId, err?.message || err);
    }
  }
  return created;
}

/**
 * Legacy sync publisher — prefers current user durable write when signed in.
 * @param {object} input
 */
export function publishNotification(input) {
  const uid = input.userId || currentUid();
  if (uid) {
    const shell = createNotificationShell({ ...input, userId: uid });
    void notify({ ...input, userId: uid, notificationId: shell.notificationId }).catch(() => {
      mirrorLocal(shell);
    });
    mirrorLocal(shell);
    return shell;
  }
  const local = createNotificationShell({ ...input, userId: null });
  local.notificationId = String(local.notificationId).replace(/^unt_/, "ntf_");
  mirrorLocal(local);
  return local;
}

export async function markNotificationRead(notificationId) {
  if (!notificationId) return listNotifications();
  const next = readStore().map((n) =>
    n.notificationId === notificationId
      ? { ...n, read: true, status: NOTIFICATION_STATUSES.READ, readAtMs: Date.now() }
      : n
  );
  writeStore(next);
  inboxCache = normalizeList(next);
  emitLocal({ type: "mark_read", notificationId });
  try {
    await userNotificationRepository.updateUserNotification(notificationId, {
      read: true,
      status: NOTIFICATION_STATUSES.READ,
      readAtMs: Date.now(),
    });
  } catch {
    /* local-only row */
  }
  return listNotifications();
}

export async function markAllNotificationsRead(userId) {
  const uid = userId || currentUid();
  const next = readStore().map((n) => ({
    ...n,
    read: true,
    status: NOTIFICATION_STATUSES.READ,
    readAtMs: Date.now(),
  }));
  writeStore(next);
  inboxCache = normalizeList(next);
  emitLocal({ type: "mark_all_read" });
  if (uid) {
    try {
      const rows = await userNotificationRepository.listUserNotifications(uid, { max: 100 });
      await Promise.all(
        rows
          .filter(isNotificationUnread)
          .map((n) =>
            userNotificationRepository.updateUserNotification(n.notificationId, {
              read: true,
              status: NOTIFICATION_STATUSES.READ,
              readAtMs: Date.now(),
            })
          )
      );
    } catch {
      /* ignore */
    }
  }
  return listNotifications();
}

export async function dismissNotification(notificationId) {
  if (!notificationId) return listNotifications();
  const next = readStore().filter((n) => n.notificationId !== notificationId);
  writeStore(next);
  inboxCache = normalizeList(next);
  emitLocal({ type: "dismiss", notificationId });
  try {
    await userNotificationRepository.updateUserNotification(notificationId, {
      status: NOTIFICATION_STATUSES.DISMISSED,
      dismissedAtMs: Date.now(),
      read: true,
    });
  } catch {
    /* local-only */
  }
  return listNotifications();
}

export function dismissNotificationsForRelationship(relationshipId) {
  if (!relationshipId) return listNotifications();
  const next = readStore().filter(
    (n) =>
      n?.meta?.relationshipId !== relationshipId &&
      n?.relationshipId !== relationshipId
  );
  writeStore(next);
  inboxCache = normalizeList(next);
  emitLocal({ type: "dismiss_relationship", relationshipId });
  void (async () => {
    const uid = currentUid();
    if (!uid) return;
    try {
      const rows = await userNotificationRepository.listUserNotifications(uid);
      await Promise.all(
        rows
          .filter(
            (n) =>
              n.relationshipId === relationshipId ||
              n.meta?.relationshipId === relationshipId
          )
          .map((n) =>
            userNotificationRepository.updateUserNotification(n.notificationId, {
              status: NOTIFICATION_STATUSES.DISMISSED,
              dismissedAtMs: Date.now(),
              read: true,
            })
          )
      );
    } catch {
      /* ignore */
    }
  })();
  return listNotifications();
}

export function clearNotifications() {
  writeStore([]);
  inboxCache = null;
  emitLocal({ type: "clear" });
}

export function notifyMatchComplete({ competitionId, drawId, fixture, href, userIds, userId }) {
  return notify({
    kind: NOTIFICATION_KINDS.MATCH_COMPLETE,
    competitionId,
    drawId,
    fixtureId: fixture?.fixtureId,
    entityType: "fixture",
    entityId: fixture?.fixtureId || null,
    title: "Match complete",
    body: `${fixture?.labelA || "A"} vs ${fixture?.labelB || "B"} · ${fixture?.scoreA ?? "–"}–${fixture?.scoreB ?? "–"}`,
    href,
    userIds,
    userId,
    meta: { winnerEntryId: fixture?.winnerEntryId },
  });
}

export function notifyCourtAssigned({ competitionId, drawId, fixture, href, userIds, userId }) {
  return notify({
    kind: NOTIFICATION_KINDS.COURT_ASSIGNED,
    competitionId,
    drawId,
    fixtureId: fixture?.fixtureId,
    entityType: "fixture",
    entityId: fixture?.fixtureId ? `court:${fixture.fixtureId}` : null,
    title: "Court assignment",
    body: `${fixture?.labelA || "A"} vs ${fixture?.labelB || "B"} → Court ${fixture?.court || "—"}`,
    href,
    userIds,
    userId,
  });
}

export function notifyMatchReady({
  competitionId,
  drawId,
  fixture,
  href,
  userIds,
  userId,
  dedupeKey,
}) {
  const matchup = `${fixture?.labelA || "A"} vs ${fixture?.labelB || "B"}`;
  const court = String(fixture?.court || "").trim();
  return notify({
    kind: NOTIFICATION_KINDS.MATCH_READY,
    competitionId,
    drawId,
    fixtureId: fixture?.fixtureId,
    entityType: "fixture",
    entityId: fixture?.fixtureId ? `ready:${fixture.fixtureId}` : null,
    title: court ? "You're up" : "Match ready",
    body: court ? `${matchup} — go to ${court}` : `${matchup} is ready to play`,
    href,
    userIds,
    userId,
    dedupeKey:
      dedupeKey ||
      (fixture?.fixtureId && fixture?.calledAtMs
        ? `match_ready:${fixture.fixtureId}:${fixture.calledAtMs}`
        : undefined),
  });
}

export function notifyTournamentComplete({
  competitionId,
  drawId,
  championLabel,
  href,
  userIds,
  userId,
}) {
  return notify({
    kind: NOTIFICATION_KINDS.TOURNAMENT_COMPLETE,
    competitionId,
    drawId,
    entityType: "competition",
    entityId: competitionId ? `complete:${competitionId}` : null,
    title: "Tournament complete",
    body: championLabel ? `Champion: ${championLabel}` : "All fixtures completed",
    href,
    userIds,
    userId,
  });
}

export function notifyRegistrationSubmitted({
  competitionId,
  competitionName,
  entry,
  href,
  userIds,
  userId,
}) {
  return notify({
    kind: NOTIFICATION_KINDS.REGISTRATION_SUBMITTED,
    competitionId,
    entityType: "entry",
    entityId: entry?.entryId || null,
    title: "Registration submitted",
    body: `${entry?.label || "Entry"} · ${competitionName || "Tournament"}`,
    href: href || `/browse/${competitionId}`,
    userIds,
    userId,
    meta: { entryId: entry?.entryId, status: entry?.status },
  });
}

export function notifyRegistrationConfirmed({
  competitionId,
  competitionName,
  entry,
  href,
  userIds,
  userId,
}) {
  return notify({
    kind: NOTIFICATION_KINDS.REGISTRATION_CONFIRMED,
    competitionId,
    entityType: "entry",
    entityId: entry?.entryId ? `confirmed:${entry.entryId}` : null,
    title: "Registration confirmed",
    body: `${entry?.label || "Entry"} is confirmed for ${competitionName || "the competition"}`,
    href: href || `/browse/${competitionId}`,
    userIds,
    userId,
    meta: { entryId: entry?.entryId },
  });
}

export function notifyRegistrationClosed({ competitionId, competitionName, href, userIds, userId }) {
  return notify({
    kind: NOTIFICATION_KINDS.REGISTRATION_CLOSED,
    competitionId,
    entityType: "competition",
    entityId: competitionId ? `reg_closed:${competitionId}` : null,
    title: "Registration closed",
    body: `Registration closed for ${competitionName || "the competition"}`,
    href: href || `/browse/${competitionId}`,
    userIds,
    userId,
  });
}

export function notifyTournamentStarted({ competitionId, competitionName, href, userIds, userId }) {
  return notify({
    kind: NOTIFICATION_KINDS.TOURNAMENT_STARTED,
    competitionId,
    entityType: "competition",
    entityId: competitionId ? `started:${competitionId}` : null,
    title: "Tournament started",
    body: `${competitionName || "Tournament"} is now in progress`,
    href: href || `/watch/${competitionId}/tournament`,
    userIds,
    userId,
  });
}

export function notifyAchievementEarned({
  userId,
  title,
  body,
  href,
  achievementId,
}) {
  if (!userId) return Promise.resolve([]);
  return notify({
    kind: NOTIFICATION_KINDS.ACHIEVEMENT_EARNED,
    title: title || "Achievement unlocked",
    body: body || "",
    href: href || "/my-player",
    entityType: "achievement",
    entityId: achievementId || null,
    dedupeKey: achievementId ? `achievement:${achievementId}` : null,
    userId,
  });
}

export {
  NOTIFICATION_KINDS,
  DEFAULT_NOTIFICATION_PREFERENCES,
  getNotificationRegistryEntry,
  resolveNotificationHref,
  isNotificationUnread,
  formatUnreadBadgeCount,
  groupNotificationsByDate,
  shouldHideNotificationBell,
  mergeNotificationPreferences,
};
